import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SEO } from '@/components/SEO';
import { AnimatedBackdrop } from '@/components/AnimatedBackdrop';
import { useAuth } from '@/context/AuthContext';
import { useUserProfile } from '@/hooks/useUserProfile';
import { getApiUrl } from '@/lib/api';
import { BlurText } from '@/components/BlurText';
import {
  scoreAndRankInvestors,
  PLAN_MATCH_LIMITS,
  MAX_REFRESHES,
  type ScoredInvestor,
} from '@/lib/investorScoring';
import { curatedInvestors } from '@/data/curatedInvestors';
import {
  Building2,
  MapPin,
  Mail,
  LinkedinIcon,
  Target,
  BadgeCheck,
  CheckCircle2,
  ArrowRight,
  Loader2,
  X,
  RefreshCw,
  Globe,
  DollarSign,
  TrendingUp,
  Lock,
} from 'lucide-react';

const REFRESH_COUNT_KEY = 'backr_match_refreshes';

function getRefreshCount(): number {
  try { return parseInt(localStorage.getItem(REFRESH_COUNT_KEY) ?? '0', 10) || 0; }
  catch { return 0; }
}

function setRefreshCount(n: number) {
  try { localStorage.setItem(REFRESH_COUNT_KEY, String(n)); } catch {}
}

type SwipeDirection = 'left' | 'right' | null;

function getDeckBatch(scored: ScoredInvestor[], refreshCount: number, limit: number): ScoredInvestor[] {
  if (scored.length === 0) return [];
  const offset = (refreshCount * limit) % scored.length;
  const primary = scored.slice(offset, offset + limit);
  if (primary.length >= limit) return primary;
  // Wrap around to fill the batch without repeating within this deck
  return [...primary, ...scored.slice(0, limit - primary.length)];
}

const cardVariants = {
  enter: { opacity: 0, scale: 0.96, y: 12 },
  center: { opacity: 1, scale: 1, y: 0 },
  exitLeft: { opacity: 0, x: -80, rotate: -4 },
  exitRight: { opacity: 0, x: 80, rotate: 4 },
};

export function MatchesPage() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, isSubscribed, isAdmin, isLoading: isAuthLoading } = useAuth();
  const { profile, isLoading: isProfileLoading } = useUserProfile(token);

  const [deck, setDeck] = useState<ScoredInvestor[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [swipeDir, setSwipeDir] = useState<SwipeDirection>(null);
  // refreshCount is sourced from backend; localStorage is a fallback until fetch completes
  const [refreshCount, setRefreshCountState] = useState(getRefreshCount);
  const [refreshCountReady, setRefreshCountReady] = useState(false);
  const [addingToCRM, setAddingToCRM] = useState(false);
  const [crmAdded, setCrmAdded] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [noProfile, setNoProfile] = useState(false);
  const [crmContactKeys, setCrmContactKeys] = useState<Set<string>>(new Set());
  const [matchCrmAddCount, setMatchCrmAddCount] = useState(0);
  const [matchCrmAddCountReady, setMatchCrmAddCountReady] = useState(false);
  const initialRefreshCount = useRef<number | null>(null);

  // Admin dashboard toggle: when "Simulate No Plan" is on, admins lose their pro bypass
  // so they experience the gated flow like a regular user with no subscription.
  const [simulateNoPlan] = useState(() => localStorage.getItem('backr_admin_simulate_no_plan') === 'true');
  const effectiveIsAdmin = isAdmin && !simulateNoPlan;

  const planKey = user?.subscriptionPlan ?? (effectiveIsAdmin ? 'pro' : 'starter');
  const limit = PLAN_MATCH_LIMITS[planKey] ?? PLAN_MATCH_LIMITS.starter;
  const crmAddLimit = limit;
  const refreshesLeft = MAX_REFRESHES - refreshCount;
  const isDeckDone = currentIndex >= deck.length;
  // Derived directly — no separate boolean state that can go stale
  const isDeckLocked = matchCrmAddCountReady && matchCrmAddCount >= crmAddLimit;
  const current = deck[currentIndex] as ScoredInvestor | undefined;

  // Fetch refresh count from backend (source of truth)
  useEffect(() => {
    if (!token) return;
    fetch(`${getApiUrl()}/api/user/match-refresh-count`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && typeof data.count === 'number') {
          setRefreshCountState(data.count);
          setRefreshCount(data.count);
          initialRefreshCount.current = data.count;
        } else {
          initialRefreshCount.current = getRefreshCount();
        }
      })
      .catch(() => { initialRefreshCount.current = getRefreshCount(); })
      .finally(() => setRefreshCountReady(true));
  }, [token]);

  // Fetch existing CRM contacts to detect already-added investors
  useEffect(() => {
    if (!token) return;
    fetch(`${getApiUrl()}/api/crm/contacts?limit=100`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.contacts) return;
        const keys = new Set<string>();
        for (const c of data.contacts) {
          if (c.name) keys.add(c.name.toLowerCase());
          if (c.email) keys.add(c.email.toLowerCase());
        }
        setCrmContactKeys(keys);
      })
      .catch(() => {});
  }, [token]);

  // Fetch match CRM add count from backend (source of truth)
  useEffect(() => {
    if (!token) return;
    fetch(`${getApiUrl()}/api/user/match-crm-add-count`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setMatchCrmAddCount(data.count ?? 0);
        }
      })
      .catch(() => {})
      .finally(() => setMatchCrmAddCountReady(true));
  }, [token]);

  // Build deck once profile AND refresh count are loaded
  useEffect(() => {
    if (isProfileLoading || !profile || !refreshCountReady) return;

    const hasProfile = !!(profile.stage || profile.industries?.length || profile.funding_amount);
    setNoProfile(!hasProfile);

    const unique = [...new Map(curatedInvestors.map((inv: typeof curatedInvestors[0]) => [inv.id, inv] as const)).values()];
    const scored = scoreAndRankInvestors(unique, profile);
    setDeck(getDeckBatch(scored, initialRefreshCount.current ?? 0, limit));
    setCurrentIndex(0);
    setCrmAdded(false);
    setIsReady(true);
  }, [profile, isProfileLoading, limit, refreshCountReady]);

  // Auth guard
  useEffect(() => {
    if (isAuthLoading) return;
    if (!isAuthenticated) { navigate('/onboarding'); return; }
    if (!isSubscribed && !effectiveIsAdmin) { navigate('/pricing'); }
  }, [isAuthenticated, isSubscribed, effectiveIsAdmin, isAuthLoading, navigate]);

  const handlePass = useCallback(() => {
    setSwipeDir('left');
    setCrmAdded(false);
    setTimeout(() => {
      setCurrentIndex(i => i + 1);
      setSwipeDir(null);
    }, 300);
  }, []);

  const handleAddToCRM = useCallback(async () => {
    if (!current || !token || addingToCRM || isDeckLocked) return;

    setAddingToCRM(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/crm/contacts`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: current.name,
          email: current.contactInfo || undefined,
          company: current.firm,
          title: current.role,
          stage: 'new',
          notes: `Added from RaiseBeacon Matches. Match score: ${current.matchScore}/100. Why fit: ${current.matchReasons.join(', ')}`,
          tags: ['investor-match', ...current.industries.map(i => i.toLowerCase())],
        }),
      });
      const status = res.status;
      console.log('[Matches] Match save status:', status);
      if (status === 201) {
        // Increment backend counter then re-fetch to confirm
        const countRes = await fetch(`${getApiUrl()}/api/user/match-crm-add-count`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }).catch(e => { console.error('[Matches] Count POST failed:', e); return null; });
        console.log('[Matches] Count POST response status:', countRes?.status);
        if (countRes && (countRes.ok || countRes.status === 429)) {
          const countData = await countRes.json();
          console.log('[Matches] Count data:', countData);
          setMatchCrmAddCount(countData.count ?? 0);
        } else {
          // POST failed — re-fetch current count from GET as fallback
          const getRes = await fetch(`${getApiUrl()}/api/user/match-crm-add-count`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => null);
          if (getRes?.ok) {
            const getData = await getRes.json();
            console.log('[Matches] Count GET fallback:', getData);
            setMatchCrmAddCount(getData.count ?? 0);
          }
        }
      }
      if (status === 201 || status === 409) {
        setCrmAdded(true);
        // Update local CRM keys so the badge shows immediately on next cards too
        setCrmContactKeys(prev => {
          const next = new Set(prev);
          next.add(current.name.toLowerCase());
          if (current.contactInfo) next.add(current.contactInfo.toLowerCase());
          return next;
        });
      }
    } catch {}
    finally { setAddingToCRM(false); }
  }, [current, token, addingToCRM, isDeckLocked]);

  const handleNext = useCallback(() => {
    setSwipeDir('right');
    setTimeout(() => {
      setCurrentIndex(i => i + 1);
      setSwipeDir(null);
      setCrmAdded(false);
    }, 300);
  }, []);

  const handleRefresh = useCallback(() => {
    if (refreshesLeft <= 0) return;
    const newCount = refreshCount + 1;
    setRefreshCountState(newCount);
    setRefreshCount(newCount);
    initialRefreshCount.current = newCount;
    // Persist to backend (fire-and-forget)
    if (token) {
      fetch(`${getApiUrl()}/api/user/match-refresh-count`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }

    const unique = [...new Map(curatedInvestors.map((inv: typeof curatedInvestors[0]) => [inv.id, inv] as const)).values()];
    const scored = scoreAndRankInvestors(unique, profile ?? {});
    setDeck(getDeckBatch(scored, newCount, limit));
    setCurrentIndex(0);
    setCrmAdded(false);
  }, [refreshesLeft, refreshCount, profile, limit, token]);

  const isLoading = isAuthLoading || isProfileLoading || !isReady || !matchCrmAddCountReady;

  if (isLoading) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-[#2B7FFF] animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground text-sm">Finding your best matches...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO title="Your Matches — RaiseBeacon" description="Curated investor matches for your startup." />
      <AnimatedBackdrop />
      <div className="min-h-screen">

        {/* Header */}
        <section className="pt-8 pb-4">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-2 mb-1">
              <BadgeCheck className="w-4 h-4 text-[#2B7FFF]" strokeWidth={2} />
              <span className="text-muted-foreground text-sm">Curated for your startup</span>
            </div>
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground dark:text-white">
                  Your investor matches
                </h1>
                {noProfile && (
                  <p className="text-amber-500 text-sm mt-1 flex items-center gap-1.5">
                    <Target className="w-3.5 h-3.5" />
                    Complete your profile for personalized scoring
                  </p>
                )}
              </div>

              {/* Plan badge + progress */}
              <div className="flex items-center gap-3 text-sm text-muted-foreground shrink-0">
                <Badge variant="secondary" className="bg-[#2B7FFF]/10 text-[#2B7FFF] border-0 font-medium capitalize">
                  {planKey}
                </Badge>
                <span className={`tabular-nums text-xs ${isDeckLocked ? 'text-amber-500 font-medium' : ''}`}>
                  {matchCrmAddCount}/{crmAddLimit} adds
                </span>
                {!isDeckDone && !isDeckLocked && (
                  <span className="tabular-nums">
                    {currentIndex + 1} / {deck.length}
                  </span>
                )}
              </div>
            </div>

            {/* Progress bar */}
            {!isDeckDone && (
              <div className="mt-4 h-1 bg-border rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-[#2B7FFF] rounded-full"
                  animate={{ width: `${((currentIndex + 1) / deck.length) * 100}%` }}
                  transition={{ type: 'spring', stiffness: 120, damping: 20 }}
                />
              </div>
            )}
          </div>
        </section>

        {/* Card area */}
        <section className="pb-20">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">
            <AnimatePresence mode="wait">
              {isDeckLocked ? (
                <motion.div
                  key="locked"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="mt-8"
                >
                  <LockedScreen
                    planKey={planKey}
                    crmAddLimit={crmAddLimit}
                    onViewCRM={() => navigate('/crm')}
                    onUpgrade={() => navigate('/pricing')}
                  />
                </motion.div>
              ) : isDeckDone ? (
                <motion.div
                  key="done"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="mt-8"
                >
                  <DoneScreen
                    refreshesLeft={refreshesLeft}
                    planKey={planKey}
                    onRefresh={handleRefresh}
                    onUpgrade={() => navigate('/pricing')}
                    onViewCRM={() => navigate('/crm')}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key={current?.id}
                  variants={cardVariants}
                  initial="enter"
                  animate={swipeDir ? (swipeDir === 'left' ? 'exitLeft' : 'exitRight') : 'center'}
                  transition={{ duration: 0.28, ease: 'easeInOut' }}
                  className="mt-6"
                >
                  {current && (() => {
                    const alreadyInCRM =
                      crmContactKeys.has(current.name.toLowerCase()) ||
                      (!!current.contactInfo && crmContactKeys.has(current.contactInfo.toLowerCase()));
                    return (
                      <InvestorCard
                        investor={current}
                        addingToCRM={addingToCRM}
                        crmAdded={crmAdded}
                        alreadyInCRM={alreadyInCRM}
                        unlocked={crmAdded || alreadyInCRM}
                        onPass={handlePass}
                        onAddToCRM={handleAddToCRM}
                        onNext={handleNext}
                      />
                    );
                  })()}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Upgrade nudge */}
            {!isDeckDone && planKey !== 'pro' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2 }}
                className="mt-6 flex items-center justify-between gap-3 px-4 py-3 bg-card border border-border rounded-xl shadow-sm"
              >
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Lock className="w-4 h-4" />
                  <span>Unlock more matches on higher plans</span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate('/pricing')}
                  className="shrink-0 text-xs border-border"
                >
                  Upgrade
                </Button>
              </motion.div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}

// ─── Investor Card ────────────────────────────────────────────────────────────

interface InvestorCardProps {
  investor: ScoredInvestor;
  addingToCRM: boolean;
  crmAdded: boolean;
  alreadyInCRM: boolean;
  unlocked: boolean;
  onPass: () => void;
  onAddToCRM: () => void;
  onNext: () => void;
}

function InvestorCard({ investor, addingToCRM, crmAdded, alreadyInCRM, unlocked, onPass, onAddToCRM, onNext }: InvestorCardProps) {
  const scoreColor =
    investor.matchScore >= 70 ? 'text-emerald-500' :
    investor.matchScore >= 40 ? 'text-amber-500' :
    'text-muted-foreground';

  const scoreBg =
    investor.matchScore >= 70 ? 'bg-emerald-500/10 border-emerald-500/20' :
    investor.matchScore >= 40 ? 'bg-amber-500/10 border-amber-500/20' :
    'bg-muted border-border';

  return (
    <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
      {/* Top strip: match score */}
      <div className="flex items-center justify-between px-6 pt-5 pb-0">
        <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-semibold ${scoreBg} ${scoreColor}`}>
          <TrendingUp className="w-3 h-3" />
          {investor.matchScore}% match
        </div>
        <Badge variant="secondary" className="bg-muted text-muted-foreground dark:bg-slate-700 dark:text-slate-300 text-xs">
          {investor.stage}
        </Badge>
      </div>

      {/* Main info */}
      <div className="px-6 pt-4 pb-2">
        <h2 className="text-xl font-bold text-foreground dark:text-white">{investor.name}</h2>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-muted-foreground">
          <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{investor.firm}</span>
          <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{investor.location}</span>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm">
          {investor.checkSize && (
            <span className="flex items-center gap-1 text-emerald-500 font-medium">
              <DollarSign className="w-3.5 h-3.5" />{investor.checkSize}
            </span>
          )}
          <span className="text-muted-foreground capitalize">{investor.investorType}</span>
        </div>
      </div>

      {/* Why they fit */}
      {investor.matchReasons.length > 0 && (
        <div className="mx-6 mt-3 p-3.5 bg-[#2B7FFF]/5 border border-[#2B7FFF]/15 rounded-xl">
          <div className="flex items-start gap-2">
            <Target className="w-4 h-4 text-[#2B7FFF] shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-[#2B7FFF] mb-1 uppercase tracking-wide">Why they fit</p>
              <div className="flex flex-wrap gap-1.5">
                {investor.matchReasons.map(r => (
                  <span key={r} className="inline-flex items-center gap-1 text-xs text-foreground/80 dark:text-slate-300">
                    <CheckCircle2 className="w-3 h-3 text-[#2B7FFF]" />{r}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Industries */}
      <div className="px-6 mt-4">
        <div className="flex flex-wrap gap-1.5">
          {investor.industries.slice(0, 6).map(ind => (
            <span key={ind} className="px-2.5 py-0.5 bg-muted dark:bg-slate-700/50 text-muted-foreground dark:text-slate-300 rounded-full text-xs">
              {ind}
            </span>
          ))}
        </div>
      </div>

      {/* Bio */}
      {investor.bio && (
        <p className="px-6 mt-3 text-sm text-muted-foreground line-clamp-3 leading-relaxed">
          {investor.bio}
        </p>
      )}

      {/* Contact links */}
      <div className="px-6 mt-4 flex flex-wrap gap-2">
        {investor.contactInfo && !investor.contactInfo.includes('@example') && (
          unlocked ? (
            <a
              href={`mailto:${investor.contactInfo}`}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-background border border-border rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              onClick={e => e.stopPropagation()}
            >
              <Mail className="w-3.5 h-3.5" />{investor.contactInfo}
            </a>
          ) : (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-background border border-border rounded-lg text-xs text-muted-foreground pointer-events-none select-none blur-sm opacity-60">
              <Mail className="w-3.5 h-3.5" /><BlurText>{investor.contactInfo}</BlurText>
            </div>
          )
        )}
        {investor.linkedin && (
          unlocked ? (
            <a
              href={investor.linkedin.startsWith('http') ? investor.linkedin : `https://${investor.linkedin}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-background border border-border rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              onClick={e => e.stopPropagation()}
            >
              <LinkedinIcon className="w-3.5 h-3.5" />LinkedIn
            </a>
          ) : (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-background border border-border rounded-lg text-xs text-muted-foreground pointer-events-none select-none blur-sm opacity-60">
              <LinkedinIcon className="w-3.5 h-3.5" />LinkedIn
            </div>
          )
        )}
        {investor.website && (
          unlocked ? (
            <a
              href={investor.website.startsWith('http') ? investor.website : `https://${investor.website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-background border border-border rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              onClick={e => e.stopPropagation()}
            >
              <Globe className="w-3.5 h-3.5" />Website
            </a>
          ) : (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-background border border-border rounded-lg text-xs text-muted-foreground pointer-events-none select-none blur-sm opacity-60">
              <Globe className="w-3.5 h-3.5" />Website
            </div>
          )
        )}
      </div>
      {!unlocked && (investor.contactInfo || investor.linkedin || investor.website) && (
        <p className="px-6 mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Lock className="w-3 h-3" />
          Save to your network to reveal contact info
        </p>
      )}

      {/* Action buttons */}
      <div className="px-6 py-5 mt-4 border-t border-border flex items-center gap-3">
        <Button
          variant="outline"
          onClick={onPass}
          className="flex-1 border-border text-muted-foreground hover:text-foreground hover:bg-muted gap-2"
        >
          <X className="w-4 h-4" />
          Pass
        </Button>

        {crmAdded || alreadyInCRM ? (
          <Button
            onClick={onNext}
            className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white gap-2"
          >
            <CheckCircle2 className="w-4 h-4" />
            {alreadyInCRM && !crmAdded ? 'In network — Next' : 'Added — Next'}
            <ArrowRight className="w-4 h-4" />
          </Button>
        ) : (
          <Button
            onClick={onAddToCRM}
            disabled={addingToCRM}
            className="flex-1 bg-[#2B7FFF] hover:bg-[#1A5FCC] text-white gap-2"
          >
            {addingToCRM ? (
              <><Loader2 className="w-4 h-4 animate-spin" />Adding...</>
            ) : (
              <>Save to network <ArrowRight className="w-4 h-4" /></>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Locked Screen ───────────────────────────────────────────────────────────

interface LockedScreenProps {
  planKey: string;
  crmAddLimit: number;
  onViewCRM: () => void;
  onUpgrade: () => void;
}

function LockedScreen({ planKey, crmAddLimit, onViewCRM, onUpgrade }: LockedScreenProps) {
  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-2xl p-8 text-center shadow-sm">
        <div className="w-14 h-14 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Lock className="w-7 h-7 text-amber-500" />
        </div>
        <h2 className="text-xl font-bold text-foreground dark:text-white mb-1">
          You've reached your match limit
        </h2>
        <p className="text-muted-foreground text-sm">
          Your <span className="font-medium capitalize">{planKey}</span> plan includes {crmAddLimit} investor saves from matches.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 mt-6 justify-center">
          <Button
            onClick={onViewCRM}
            className="bg-[#2B7FFF] hover:bg-[#1A5FCC] text-white gap-2"
          >
            Open workspace
            <ArrowRight className="w-4 h-4" />
          </Button>
          {planKey !== 'pro' && (
            <Button variant="outline" onClick={onUpgrade} className="border-border gap-2">
              <TrendingUp className="w-4 h-4" />
              Upgrade plan
            </Button>
          )}
        </div>
      </div>

      <div className="p-4 bg-amber-500/5 border border-amber-500/10 rounded-xl flex items-start gap-3">
        <CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground dark:text-white">Keep going: </span>
          The investors you've added are waiting in your workspace — start reaching out today.
        </p>
      </div>
    </div>
  );
}

// ─── Done Screen ──────────────────────────────────────────────────────────────

interface DoneScreenProps {
  refreshesLeft: number;
  planKey: string;
  onRefresh: () => void;
  onUpgrade: () => void;
  onViewCRM: () => void;
}

function DoneScreen({ refreshesLeft, planKey, onRefresh, onUpgrade, onViewCRM }: DoneScreenProps) {
  return (
    <div className="space-y-4">
      {/* Completion card */}
      <div className="bg-card border border-border rounded-2xl p-8 text-center shadow-sm">
        <div className="w-14 h-14 bg-[#2B7FFF]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-7 h-7 text-[#2B7FFF]" />
        </div>
        <h2 className="text-xl font-bold text-foreground dark:text-white mb-1">
          You've reviewed all your matches
        </h2>
        <p className="text-muted-foreground text-sm">
          Open your workspace to manage the investors you added.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 mt-6 justify-center">
          <Button
            onClick={onViewCRM}
            className="bg-[#2B7FFF] hover:bg-[#1A5FCC] text-white gap-2"
          >
            Open workspace
            <ArrowRight className="w-4 h-4" />
          </Button>

          {refreshesLeft > 0 ? (
            <Button variant="outline" onClick={onRefresh} className="border-border gap-2">
              <RefreshCw className="w-4 h-4" />
              Refresh list
              <span className="text-xs text-muted-foreground">({refreshesLeft} left)</span>
            </Button>
          ) : (
            <Button variant="outline" disabled className="border-border gap-2 opacity-50">
              <RefreshCw className="w-4 h-4" />
              No refreshes left
            </Button>
          )}
        </div>
      </div>

      {/* Upgrade CTA if not on pro */}
      {planKey !== 'pro' && (
        <div className="bg-card border border-border rounded-xl p-5 flex items-center justify-between gap-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 bg-[#2B7FFF]/10 rounded-lg flex items-center justify-center shrink-0">
              <TrendingUp className="w-4 h-4 text-[#2B7FFF]" />
            </div>
            <div>
              <p className="font-semibold text-foreground dark:text-white text-sm">Want more matches?</p>
              <p className="text-muted-foreground text-xs mt-0.5">
                {planKey === 'starter' ? 'Upgrade to Fundraise for 25 investors, or Pro for 50.' : 'Upgrade to Pro for 50 curated investors.'}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={onUpgrade}
            className="bg-[#2B7FFF] hover:bg-[#1A5FCC] text-white shrink-0"
          >
            Upgrade
          </Button>
        </div>
      )}

      {/* Pro tip */}
      <div className="p-4 bg-emerald-500/5 border border-emerald-500/10 rounded-xl flex items-start gap-3">
        <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground dark:text-white">Pro tip: </span>
          Reference why they fit in your first email — it increases response rates by 3x.
        </p>
      </div>
    </div>
  );
}
